import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useUnsavedWarning from '../../../hooks/useUnsavedWarning';
import Button from '../../../components/Button';
import TextInput from '../../../components/TextInput';
import { Phone, Mail, MapPin, Globe } from 'lucide-react';
import { getContactDetails, saveContactDetails } from '../../../api';
import PageLoader from '../../../components/PageLoader';
import { EMAIL, MOBILE_INDIA, VALID_LINK, testRegex } from '../../../config/regex';

const AdminCMSContactDetails = () => {
  const [form, setForm] = useState({
    _id: null,
    facebook_link: '',
    twitter_link: '',
    instagram: '',
    address: '',
    all_course_no1: '',
    all_course_no2: '',
    trading_no: '',
    email: '',
    whatsapp: '',
  });
  const [initialForm, setInitialForm] = useState(null);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const isChanged = initialForm && JSON.stringify(form) !== JSON.stringify(initialForm);
  useUnsavedWarning(isChanged);

  const validateField = (field, value) => {
    const trimmed = String(value ?? '').trim();
    switch (field) {
      case 'facebook_link':
      case 'twitter_link':
      case 'instagram':
        if (!trimmed) return 'Required';
        if (!testRegex(VALID_LINK, trimmed)) return 'Enter a valid URL (http/https)';
        return '';
      case 'address':
        if (!trimmed) return 'Address is required';
        return '';
      case 'email':
        if (!trimmed) return 'Email is required';
        if (!testRegex(EMAIL, trimmed)) return 'Enter a valid email';
        return '';
      case 'all_course_no1':
      case 'trading_no':
      case 'whatsapp': {
        if (!trimmed) return 'Number is required';
        if (!testRegex(MOBILE_INDIA, trimmed)) return 'Enter a valid Indian mobile number';
        return '';
      }
      case 'all_course_no2': {
        if (!trimmed) return '';
        if (!testRegex(MOBILE_INDIA, trimmed)) return 'Enter a valid Indian mobile number';
        return '';
      }
      default:
        return '';
    }
  };

  useEffect(() => {
    let mounted = true;

    const fetchContact = async () => {
      try {
        const stored = await getContactDetails();
        if (!mounted || !stored) return;

        const data = {
          _id: stored._id || null,
          facebook_link: stored.facebook_link || '',
          twitter_link: stored.twitter_link || '',
          instagram: stored.instagram || '',
          address: stored.address || '',
          all_course_no1: stored.all_course_no1 ?? '',
          all_course_no2: stored.all_course_no2 ?? '',
          trading_no: stored.trading_no ?? '',
          email: stored.email || '',
          whatsapp: stored.whatsapp || '',
        };
        setForm(data);
        setInitialForm(data);
        setError('');
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'No contact details found yet. Add and save to create.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchContact();

    return () => {
      mounted = false;
    };
  }, []);

  const normalizePhone = (value) =>
    String(value ?? '')
      .replace(/\D/g, '')
      .slice(0, 10);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({
      ...prev,
      [field]: validateField(field, value),
    }));
  };

  const updatePhoneField = (field, value) => {
    const normalized = normalizePhone(value);
    updateField(field, normalized);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // validate all fields before sending to API
      const nextErrors = {};
      Object.entries(form).forEach(([key, val]) => {
        if (key === '_id') return;
        const errMsg = validateField(key, val);
        if (errMsg) nextErrors[key] = errMsg;
      });
      if (Object.keys(nextErrors).length) {
        setFieldErrors(nextErrors);
        return;
      }

      const payload = {
        _id: form._id,
        facebook_link: form.facebook_link || '',
        twitter_link: form.twitter_link || '',
        instagram: form.instagram || '',
        address: form.address || '',
        all_course_no1: form.all_course_no1,
        all_course_no2: form.all_course_no2,
        trading_no: form.trading_no,
        email: form.email || '',
        whatsapp: form.whatsapp || '',
      };

      const saved = await saveContactDetails(payload);

      const data = {
        _id: saved._id || null,
        facebook_link: saved.facebook_link || '',
        twitter_link: saved.twitter_link || '',
        instagram: saved.instagram || '',
        address: saved.address || '',
        all_course_no1: saved.all_course_no1 ?? '',
        all_course_no2: saved.all_course_no2 ?? '',
        trading_no: saved.trading_no ?? '',
        email: saved.email || '',
        whatsapp: saved.whatsapp || '',
      };

      setForm(data);
      setInitialForm(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save contact details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <Link to="/admin/cms" className="text-sm text-primary-600 hover:underline mb-1 inline-block">
            ← CMS Pages
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">Contact Info</h1>
          <p className="text-gray-600 text-sm mt-1">
            Manage phone numbers, email, address, and social links used on the public Contact page.
          </p>
        </div>
        <Button 
          variant="primary" 
          size="sm" 
          onClick={handleSave} 
          disabled={saving || !isChanged}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-6 max-w-2xl">
        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white shadow-sm">
          <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Globe size={18} className="text-primary-500" /> Social Links
          </h2>
          <div className="space-y-4">
            <TextInput
              label="Facebook URL"
              value={form.facebook_link}
              onChange={(e) => updateField('facebook_link', e.target.value)}
              placeholder="https://facebook.com/..."
              error={fieldErrors.facebook_link}
              required
            />
            <TextInput
              label="Twitter (X) URL"
              value={form.twitter_link}
              onChange={(e) => updateField('twitter_link', e.target.value)}
              placeholder="https://twitter.com/..."
              error={fieldErrors.twitter_link}
              required
            />
            <TextInput
              label="Instagram URL"
              value={form.instagram}
              onChange={(e) => updateField('instagram', e.target.value)}
              placeholder="https://instagram.com/..."
              error={fieldErrors.instagram}
              required
            />
          </div>
        </section>

        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white shadow-sm">
          <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <MapPin size={18} className="text-primary-500" /> Office Address
          </h2>
          <TextInput
            label="Street Address"
            value={form.address}
            onChange={(e) => updateField('address', e.target.value)}
            placeholder="Full office address"
            multiline
            rows={3}
            error={fieldErrors.address}
            required
          />
        </section>

        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white shadow-sm">
          <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Phone size={18} className="text-primary-500" /> Contact Numbers
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextInput
              label="Primary Helpdesk"
              value={form.all_course_no1}
              onChange={(e) => updatePhoneField('all_course_no1', e.target.value)}
              placeholder="e.g. 7480080039"
              error={fieldErrors.all_course_no1}
              inputMode="numeric"
              maxLength={10}
              required
            />
            <TextInput
              label="Secondary Helpdesk"
              value={form.all_course_no2}
              onChange={(e) => updatePhoneField('all_course_no2', e.target.value)}
              placeholder="Optional"
              error={fieldErrors.all_course_no2}
              inputMode="numeric"
              maxLength={10}
            />
            <TextInput
              label="Trading Support"
              value={form.trading_no}
              onChange={(e) => updatePhoneField('trading_no', e.target.value)}
              placeholder="e.g. 9890115928"
              error={fieldErrors.trading_no}
              inputMode="numeric"
              maxLength={10}
              required
            />
            <TextInput
              label="WhatsApp Number"
              value={form.whatsapp}
              onChange={(e) => updatePhoneField('whatsapp', e.target.value)}
              placeholder="e.g. 917480080039"
              error={fieldErrors.whatsapp}
              inputMode="numeric"
              maxLength={10}
              required
            />
          </div>
        </section>

        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white shadow-sm">
          <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Mail size={18} className="text-primary-500" /> Email Support
          </h2>
          <TextInput
            label="Support Email"
            value={form.email}
            onChange={(e) => updateField('email', e.target.value)}
            placeholder="support@marketmavericks.com"
            error={fieldErrors.email}
            required
          />
        </section>
      </div>
    </div>
  );
};

export default AdminCMSContactDetails;

